/**
 * This is a temporary feature to satisfy the Nigeria RFI 04/10/24.
 * It should not be released to the public.
 *
 * Should we wish to productionise, we must:
 * - Move the database to RxDB
 * - Sync the files
 * - Add a sync indicator to the UI
 * - Save the real user ID and fetch their name from network/RxDB
 * - Add full test suite
 */

import { Button } from "$components/button/button";
import {
  Card,
  CardBody,
  CardColors,
  CardDetails,
  CardSubtitle,
  CardSummary,
  CardTitle,
} from "$components/card";
import { useUser } from "$global-providers/user-provider";
import { randomId } from "$routes/_lib/random-id";
import type { HistoricPatientEvent } from "$routes/patient/[:patientId]/overview/history/_lib/types";
import { Typography } from "@material-ui/core";
import { type DBSchema, openDB } from "idb";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

export type PatientHistoryFile = {
  id: string;
  filePath: string;
  fileName: string;
  patientId: string;
  meta: { created: string };
};

export function isPatientHisoryFile(
  cardEntity: HistoricPatientEvent,
): cardEntity is PatientHistoryFile {
  return "filePath" in cardEntity;
}

interface PatientHistoryDB extends DBSchema {
  files: {
    key: string;
    value: PatientHistoryFile;
    indexes: { "by-patient": string };
  };
}

const dbPromise = openDB<PatientHistoryDB>("PatientHistoryFilesDB", 1, {
  upgrade(db) {
    const store = db.createObjectStore("files", {
      keyPath: "id",
    });
    store.createIndex("by-patient", "patientId");
  },
});

// Function to save a file to local storage
export const uploadFile = (
  file: File,
): Promise<{ fileName: string; fileContent: string }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const fileContent = event.target.result as string;

        try {
          resolve({
            fileContent: fileContent,
            fileName: file.name,
          });
        } catch (_error) {
          reject(new Error("Failed to save file to local storage"));
        }
      } else {
        reject(new Error("Failed to read file"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const addPatientHistoryFile = async (file: PatientHistoryFile) => {
  const db = await dbPromise;
  await db.add("files", file);
  // Notify any mounted `usePatientHistoryFiles` instances to reload. Errors
  // propagate to the caller (the upload flow reports them).
  window.dispatchEvent(new Event("patient-history-files-updated"));
};

export const usePatientHistoryFiles = ({
  patientId,
}: {
  patientId: string;
}) => {
  const [files, setFiles] = useState<PatientHistoryFile[]>([]);

  useEffect(() => {
    const loadFiles = async () => {
      const db = await dbPromise;
      const tx = db.transaction("files", "readonly");
      const index = tx.store.index("by-patient");
      const patientFiles = await index.getAll(patientId);
      setFiles([...patientFiles]);
    };

    loadFiles();

    // Set up event listener for database changes
    const handleDatabaseChange = () => {
      loadFiles();
    };

    window.addEventListener(
      "patient-history-files-updated",
      handleDatabaseChange,
    );

    return () => {
      window.removeEventListener(
        "patient-history-files-updated",
        handleDatabaseChange,
      );
    };
  }, [patientId]);

  return { files, addFile: addPatientHistoryFile };
};

/**
 * Wires the hidden file input and its upload handler so a caller can trigger the
 * picker from anywhere (e.g. an action-bar item). Render `fileInput` somewhere
 * in the tree and call `openFilePicker` to prompt for a file.
 */
export const usePatientHistoryFileUpload = (patientId: string) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    // Reset so re-picking the same file still fires a `change` event.
    input.value = "";
    if (file) {
      uploadFile(file)
        .then((fileData) =>
          addPatientHistoryFile({
            id: randomId(),
            filePath: fileData.fileContent,
            fileName: fileData.fileName,
            patientId,
            meta: { created: new Date().toISOString() },
          }),
        )
        .catch((error) => {
          console.error("Error saving file:", error);
        });
    }
  };

  const fileInput = (
    <input
      type="file"
      ref={fileInputRef}
      style={{ display: "none" }}
      accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.jpg,.jpeg,.png,.gif,.bmp,.tiff"
      onChange={handleFileChange}
    />
  );

  return { openFilePicker, fileInput };
};

export const PatientHistoryFileCard: FC<{
  filePath: string;
  fileName: string;
}> = ({ filePath, fileName }) => {
  const color = CardColors.CONSULTATION;
  const user = useUser();

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card color={color} expandable>
      <CardBody>
        <CardSummary>
          <CardSubtitle color={color} gutterBottom>
            <FormattedMessage
              id="patient-history.card.file.title"
              defaultMessage="Document"
            />
          </CardSubtitle>
          <CardTitle>{fileName}</CardTitle>
        </CardSummary>
        <CardDetails>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" component="p">
              <strong>
                <FormattedMessage
                  id="patient.prescriptions.dispense.prescription.prescribedBy"
                  defaultMessage="Uploaded by:"
                />
              </strong>
              &nbsp;
              {`${user.firstName} ${user.lastName}`}
            </Typography>
            <Button
              color="secondary"
              onClick={handleDownload}
              aria-label="Download File"
            >
              <FormattedMessage
                id="patient-history.card.file.download"
                defaultMessage="Download"
              />
            </Button>
          </div>
        </CardDetails>
      </CardBody>
    </Card>
  );
};
